import { JOURNAL_COLOURS } from "../../services/journalColours";
import { Button } from "../common/Button";
import { Field } from "../common/Field";
import { Toggle } from "../common/Toggle";
import { CollapsibleChips } from "./CollapsibleChips";

/**
 * The filter bar over the journal (#464, #466).
 *
 * SEARCH IS FIRST, and that ordering is the argument. Tags and colours
 * only help entries somebody remembered to file; search helps the other
 * kind, which is most of them. The recall people actually have is a word
 * they wrote -- "that yard with the aggressive hive" -- not a label they
 * assigned six months ago.
 *
 * ALL OF IT IS OPTIONAL AND NONE OF IT IS STICKY-BY-DEFAULT. A journal
 * opened tomorrow shows the journal, not yesterday's filter with three
 * entries in it and no obvious reason why. The "Clear" button exists for
 * the same reason and is shown only when something is actually on --
 * a permanently visible Clear on an unfiltered list is a control that
 * does nothing, and those teach people to ignore controls.
 *
 * ── WHOSE TAGS THIS OFFERS, AND A REVERSED CALL ──
 *
 * This said the tag list was the caller's OWN, on the argument that
 * "filtering and filing are about your own vocabulary". That was decided
 * before anyone used it, and it left the half of #477 that mattered
 * still broken: V119 made a teammate's tags visible on a shared entry,
 * so you could READ a tag and not filter by it. The objection that
 * reversed V118 was about filtering in the first place --
 *
 *     "If the users can't tag entries then the other team members can't
 *      filter or search on those tags in the shared entries."
 *
 * -- so offering tags you can see but cannot act on answered the
 * narrower half of it and left the stated reason unmet.
 *
 * FILING and FILTERING come apart here, which is why the earlier note
 * conflated them. Filing is a write and stays the author's: V119's BLOCK
 * predicate still refuses anyone else, and the manage panel is still
 * built from list_tags alone, because retiring somebody else's label is
 * a control that would refuse. Filtering is a read, and the right scope
 * for a read is what you can already see.
 *
 * So `tags` is now whatever JournalPage judges filterable -- your own
 * plus those met on visible entries. This component does not decide it;
 * see filterableTags there for the scope and its bound.
 */
export function JournalFilters({ value, tags, authors = [], onChange, onManageTags }) {
  const { search = "", colour = null, tagIds = [], authorIds = [], pinned = false,
          dateFrom = "", dateTo = "" } = value;

  const set = (patch) => onChange({ ...value, ...patch });

  const active = Boolean(search || colour || tagIds.length || authorIds.length || pinned || dateFrom || dateTo);

  const toggleTag = (tagId) =>
    set({ tagIds: tagIds.includes(tagId)
      ? tagIds.filter((t) => t !== tagId)
      : [...tagIds, tagId] });

  /* ACTIVE FIRST, IN THE ORDER THEY WERE CHOSEN. Deriving it from
     `tagIds` rather than filtering `tags` keeps the row stable: a tag
     does not jump position because the available list reordered
     underneath it. Any id with no matching tag is dropped rather than
     rendered nameless -- a retired tag still in the filter is already
     handled by JournalPage, and a blank chip would be worse than its
     absence. */
  const byId = new Map(tags.map((t) => [t.tag_id, t]));
  const activeTags = tagIds.map((id) => byId.get(id)).filter(Boolean);
  const availableTags = tags.filter((t) => !tagIds.includes(t.tag_id));

  /* ONE chip, two rows. Identical but for which way it goes, and the
     accessible name says that -- "Remove tag X" against "Filter by X"
     -- because a row of chips that all read "X" tells somebody
     navigating by name nothing about what pressing one does. */
  const chip = (t, isActive) => (
    <button
      key={t.tag_id}
      type="button"
      aria-label={isActive ? `Remove tag ${t.name}` : `Filter by ${t.name}`}
      aria-pressed={isActive}
      onClick={() => toggleTag(t.tag_id)}
      className="touch-target"
      style={{
        fontSize: "var(--font-size-sm)",
        borderRadius: "var(--radius-sm)",
        padding: "2px 8px",
        cursor: "pointer",
        border: "1px solid var(--color-border)",
        background: isActive
          ? "var(--color-accent-muted, var(--color-surface-2))"
          : "transparent",
        color: "var(--color-text)",
      }}
    >
      {t.name}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <Field label="Search your entries">
        {(id, aria) => (
          <input
            id={id}
            {...aria}
            type="search"
            className="touch-target"
            style={{ width: "100%" }}
            value={search}
            onChange={(e) => set({ search: e.target.value })}
            placeholder="A word you remember writing…"
          />
        )}
      </Field>

      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
          Colour
        </span>
        {JOURNAL_COLOURS.map((c) => (
          <button
            key={c.key}
            type="button"
            /* The colour is shown AND named. A row of coloured dots is
               unusable for anyone who cannot tell them apart, and an
               accessible name is the only thing standing between this
               filter and being decoration. */
            aria-label={c.label}
            aria-pressed={colour === c.key}
            onClick={() => set({ colour: colour === c.key ? null : c.key })}
            style={{
              width: 24, height: 24, borderRadius: "50%",
              background: c.css,
              border: colour === c.key
                ? "3px solid var(--color-text)"
                : "1px solid var(--color-border)",
              cursor: "pointer",
            }}
          />
        ))}
      </div>

      {tags.length > 0 && (
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
            Tags
          </span>
          {/* THE SAME CONTROL AS THE ENTRY ROW. Two ways to pick a tag
              on one screen is how people learn that one of them does
              something different.

              ACTIVE AND AVAILABLE ARE NOW SEPARATE ROWS, asked for from
              testing: "it might be better if the active tags were
              separate from the available to select tags. The active tags
              also do not collapse even when I choose 16."

              Those two observations have one cause. CollapsibleChips
              pulls selected chips to the front and never hides one, so
              the limit only ever applies to the REST -- which is correct
              while the two kinds share a row, because a filter you
              cannot see is a list narrowed with no visible control to
              turn it off. Sixteen active tags then means sixteen chips
              and no collapse available to it.

              Splitting them removes the reason for that rule on each
              row: nothing in the Active row is "hidden while applied"
              when the row IS the applied set and says how many it holds.
              So both rows collapse on the ordinary limit, and the count
              in the heading is what keeps a collapsed Active row
              honest. */}
          {activeTags.length > 0 && (
            <>
              <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
                Active ({activeTags.length})
              </span>
              <CollapsibleChips
                items={activeTags}
                isSelected={() => false}
                moreLabel="active"
                renderItem={(t) => chip(t, true)}
              />
            </>
          )}

          {availableTags.length > 0 && (
            <>
              <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
                {activeTags.length > 0 ? "Add" : "Tags"}
              </span>
              <CollapsibleChips
                items={availableTags}
                isSelected={() => false}
                moreLabel="tags"
                renderItem={(t) => chip(t, false)}
              />
            </>
          )}

          {/* Said out loud because it is the opposite of what most
              filter chips do, and a user who assumes OR will think the
              second tag broke the list. */}
          {tagIds.length > 1 && (
            <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
              showing entries with all {tagIds.length}
            </span>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        {/* Only on the team view and only when there is someone to choose
            between (#499). Multi-select as asked: a disclosure of
            checkboxes, which is a drop-down that stays open while you
            tick more than one. */}
        {authors.length > 1 && (
          <details>
            <summary className="touch-target" style={{ cursor: "pointer" }}>
              {authorIds.length ? `Written by (${authorIds.length})` : "Written by"}
            </summary>
            <div role="group" aria-label="Written by"
                 style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
              {authors.map((a) => (
                <label key={a.id} style={{ display: "flex", gap: "var(--space-2)" }}>
                  <input
                    type="checkbox"
                    id={`journal-author-${a.id}`}
                    name="journal-author"
                    checked={authorIds.includes(a.id)}
                    onChange={() => set({ authorIds: authorIds.includes(a.id)
                      ? authorIds.filter((i) => i !== a.id)
                      : [...authorIds, a.id] })}
                  />
                  {a.label}
                </label>
              ))}
            </div>
          </details>
        )}
        <Field label="From">
          {(id, aria) => (
            <input id={id} {...aria} type="date" className="touch-target"
                   value={dateFrom} onChange={(e) => set({ dateFrom: e.target.value })} />
          )}
        </Field>
        <Field label="To">
          {(id, aria) => (
            <input id={id} {...aria} type="date" className="touch-target"
                   value={dateTo} onChange={(e) => set({ dateTo: e.target.value })} />
          )}
        </Field>
        {/* The app's own control, not a bare browser checkbox (#474).
            This was the only unstyled input on the screen, which made
            one new feature look bolted on to a screen that is about to
            be the most-used in the app. Toggle is what every other
            binary control here uses. */}
        <Toggle
          label="Pinned only"
          checked={pinned}
          onChange={(next) => set({ pinned: next })}
        />
        <Button size="sm" variant="secondary" onClick={onManageTags}>
          Manage tags
        </Button>
        {active && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onChange({ search: "", colour: null, tagIds: [], authorIds: [],
                                      pinned: false, dateFrom: "", dateTo: "" })}
          >
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
}
